import { APP_ROUTE_PATHS, PRODUCT_PATHS } from '@missions/types';

/**
 * Where is this app running? (PLAN §7.2)
 *  - a tenant's own domain (tasks.acme.com)                → slug null, router basename "/"
 *  - the temporary link missions.devninjaweb.com/<slug>/…  → slug from the first path segment, basename "/<slug>"
 *  - local dev (localhost)                                 → no path slug (deep links like /inbox must work);
 *    the tenant is sent as the VITE_DEV_TENANT_SLUG hint against the temp host (default "devninjaweb").
 */
const TEMP_HOST = (import.meta.env.VITE_TEMP_LINK_HOST ?? 'missions.devninjaweb.com').toLowerCase();
const DEV_SLUG = import.meta.env.VITE_DEV_TENANT_SLUG ?? 'devninjaweb';
/** Product pages on the temp host that are never tenant slugs (served without a workspace). */
const PRODUCT = new Set<string>(PRODUCT_PATHS);
/** Tenant app routes: on the temp host they only make sense AFTER a slug (/<slug>/login). */
const APP_ROUTES = new Set<string>(APP_ROUTE_PATHS);
const SLUG_RE = /^[a-z0-9-]{3,40}$/;

export interface TenantLocation {
  host: string;
  slug: string | null;
  basename: string;
}

type Loc = Pick<Location, 'hostname' | 'pathname'>;

const firstSegment = (pathname: string): string => pathname.split('/')[1]?.toLowerCase() ?? '';

export function tenantLocation(loc: Loc = window.location): TenantLocation {
  const host = loc.hostname.toLowerCase();
  if (host === 'localhost' || host === '127.0.0.1') return { host: TEMP_HOST, slug: DEV_SLUG, basename: '/' };
  if (host !== TEMP_HOST) return { host, slug: null, basename: '/' };
  const first = firstSegment(loc.pathname);
  const slug = first && !PRODUCT.has(first) && !APP_ROUTES.has(first) && SLUG_RE.test(first) ? first : null;
  return { host, slug, basename: slug ? `/${slug}` : '/' };
}

/** localStorage key: the last workspace opened on the temp host, so the bare root can send people back. */
export const LAST_SLUG_KEY = 'missions.lastWorkspace';

/** True on the bare root of the temp host (no workspace slug, not a product page). */
export function isTempRoot(loc: Loc = window.location): boolean {
  return loc.hostname.toLowerCase() === TEMP_HOST && /^\/?$/.test(loc.pathname);
}

/** True when a temp-host URL starts with an app route but has no workspace slug (…/login, …/reset/<token>). */
export function isSluglessAppPath(loc: Loc = window.location): boolean {
  if (loc.hostname.toLowerCase() !== TEMP_HOST) return false;
  const first = firstSegment(loc.pathname);
  return APP_ROUTES.has(first) || first === 'p' || first === 'my';
}

/**
 * Where to send someone who landed on the temp host without a workspace in the path:
 *  - bare root or an app route (/login, /reset/abc?x=1) + a remembered workspace → "/<last>/reset/abc?x=1"
 *  - otherwise null (show the workspace picker, which keeps the rest of the path).
 * Never a dead "Workspace not found".
 */
export function sluglessRedirect(loc: Pick<Location, 'hostname' | 'pathname' | 'search' | 'hash'>, lastSlug: string | null): string | null {
  if (!lastSlug || !SLUG_RE.test(lastSlug)) return null;
  if (!isTempRoot(loc) && !isSluglessAppPath(loc)) return null;
  const rest = loc.pathname === '/' || loc.pathname === '' ? '/' : loc.pathname;
  return `/${lastSlug}${rest}${loc.search}${loc.hash}`;
}

/** The path to keep after the workspace when using the picker (/reset/abc → "/reset/abc"), or "/" for none. */
export function pathAfterPicker(loc: Pick<Location, 'hostname' | 'pathname' | 'search' | 'hash'>): string {
  return isSluglessAppPath(loc) ? `${loc.pathname}${loc.search}${loc.hash}` : '/';
}

/** Absolute in-app URL for full page loads outside the router (error screens): keeps the slug basename. */
export function appHref(path: string, loc: Loc = window.location): string {
  const base = tenantLocation(loc).basename.replace(/\/$/, '');
  return `${base}${path.startsWith('/') ? path : `/${path}`}`;
}

/** Link on the temporary host (same origin there, absolute from a custom domain). */
export function tempHostUrl(path: string, loc: Pick<Location, 'hostname'> = window.location): string {
  const p = path.startsWith('/') ? path : `/${path}`;
  return loc.hostname.toLowerCase() === TEMP_HOST || loc.hostname === 'localhost' || loc.hostname === '127.0.0.1' ? p : `https://${TEMP_HOST}${p}`;
}
